import {
  ClockIcon,
  JointAppointmentsIcon,
  NewTabIcon,
  SentBackIcon,
  SkippedToIcon,
  TickIcon,
  TooltipArrow
} from "@/features/common/Icons";
import { isStepsMoreThanEdgeCase, TooltipData } from "./WorkflowTaskProgress";
import { stepClass } from "./WorkflowTaskStep";
import { formatDateFullYear } from "@/features/common/helper";
import { useIntl } from "react-intl";
import translate from "@/i18n/translate";
import { WmButton } from "@watermarkinsights/ripple-react";
import useSmallScreenView from "@/features/common/useSmallScreenView";
import { useContext } from "react";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { Assignee } from "../ReviewsInProgress";
import {
  FIXED,
  IN_PROGRESS_STATUS,
  NOT_STARTED_STATUS,
  ON_HOLD_STATUS,
  SENT_BACK_STATUS,
  SKIPPED_STATUS,
  SUBMITTED_STATUS,
  TAB
} from "@/features/common/constants";

interface WorkflowTaskSmallScreenInterface {
  tooltipData: TooltipData | null;
  tooltipRef?: any;
  closeTooltip?: () => void;
  closeEdgeCaseTriggered?: () => void;
  stepsLength?: number;
}

export function getTaskState(state: string) {
  switch (state) {
    case SUBMITTED_STATUS:
      return useIntl().formatMessage({
        id: "app.home.reviewsInProgress.completedState"
      });
    case IN_PROGRESS_STATUS:
      return useIntl().formatMessage({
        id: "app.home.reviewsInProgress.inProgressState"
      });
    case NOT_STARTED_STATUS:
      return useIntl().formatMessage({
        id: "app.home.reviewsInProgress.notStartedState"
      });
    case SENT_BACK_STATUS:
      return useIntl().formatMessage({
        id: "app.home.reviewsInProgress.sentBack"
      });
    case SKIPPED_STATUS:
      return useIntl().formatMessage({
        id: "app.home.reviewsInProgress.skipped"
      });
    case ON_HOLD_STATUS:
      return useIntl().formatMessage({
        id: "app.home.reviewsInProgress.onHold"
      });
  }
}

export function WorkflowTaskStepTooltip({
  tooltipData,
  tooltipRef,
  closeTooltip,
  closeEdgeCaseTriggered,
  stepsLength
}: WorkflowTaskSmallScreenInterface) {
  const isSmallScreen = useSmallScreenView(1124);

  const stepIndex = useIntl().formatMessage(
    { id: "app.home.reviewsInProgress.stepIndex" },
    {
      stepIndex: tooltipData && tooltipData?.stepIndex + 1,
      totalSteps: tooltipData?.totalSteps
    }
  );

  function closeWithTab(event: React.KeyboardEvent) {
    if (event.key === TAB) {
      closeTooltip && closeTooltip();
      if (
        stepsLength &&
        isStepsMoreThanEdgeCase(stepsLength) &&
        closeEdgeCaseTriggered
      ) {
        closeEdgeCaseTriggered();
      }
    }
  }

  function handleTooltipClose() {
    if (closeTooltip) {
      const stepElement = document.querySelector(
        '.step[tabIndex="0"]'
      ) as HTMLElement;
      if (stepElement) {
        stepElement.tabIndex = -1;
      }
      closeTooltip();
      if (
        stepsLength &&
        isStepsMoreThanEdgeCase(stepsLength) &&
        closeEdgeCaseTriggered
      ) {
        closeEdgeCaseTriggered();
      }
    }
  }

  const sentBackTo = useIntl().formatMessage(
    { id: "app.home.reviewsInProgress.sentBackTo" },
    {
      name: tooltipData?.sentBackTo
    }
  );
  const skippedTo = useIntl().formatMessage(
    { id: "app.home.reviewsInProgress.skippedTo" },
    {
      name: tooltipData?.skippedTo
    }
  );
  function isStatusSubmitted(assignees: Assignee[]) {
    return assignees.some((assignee) => assignee.status === SUBMITTED_STATUS);
  }

  return (
    tooltipData && (
      <>
        <div
          className="tooltip-pointer"
          style={{ left: tooltipData?.carrotPosition + "px" }}
        >
          <TooltipArrow />
        </div>
        <div
          style={
            isSmallScreen
              ? { marginLeft: "0" }
              : { marginLeft: tooltipData?.positionX + "px" }
          }
          className={
            isSmallScreen ? "tooltip-small-screen" : "task-step-tooltip"
          }
          ref={tooltipRef}
        >
          {!isSmallScreen && (
            <>
              <div className="close-button">
                <WmButton
                  button-type="navigational"
                  tooltip-position="left"
                  class="-navigational hydrated"
                  icon="f156"
                  tooltip="Close"
                  onClick={handleTooltipClose}
                ></WmButton>
              </div>
            </>
          )}
          <div className="tooltip-header">
            {tooltipData.splitStep && isSmallScreen && (
              <JointAppointmentsIcon />
            )}

            <div
              className={`tooltip-state ${stepClass(
                tooltipData?.state
              )}-tooltip`}
            >
              {getTaskState(tooltipData?.state)}
            </div>

            {tooltipData.splitStep &&
              tooltipData.state === IN_PROGRESS_STATUS &&
              isStatusSubmitted(tooltipData.assignees) && (
                <div
                  className={`tooltip-state ${stepClass(
                    SUBMITTED_STATUS
                  )}-tooltip`}
                >
                  {getTaskState(SUBMITTED_STATUS)}
                </div>
              )}
            <div className="step-index">{`(${stepIndex})`}</div>
          </div>
          {!isSmallScreen && (
            <div className="flex-row -verticallycentered">
              <div className="step-name">{tooltipData.name}</div>
              {tooltipData.splitStep && (
                <div className="split-step-icon">
                  <JointAppointmentsIcon />
                </div>
              )}
            </div>
          )}
          {tooltipData.state !== SKIPPED_STATUS &&
            tooltipData.state !== ON_HOLD_STATUS && (
              <TooltipDate tooltipData={tooltipData} />
            )}
          {tooltipData.skippedTo && (
            <div className="skipped-to flex-row -verticallycentered">
              <SkippedToIcon /> <span>{skippedTo}</span>
            </div>
          )}
          {tooltipData.sentBackTo && (
            <div className="sent-back-to flex-row -verticallycentered">
              <SentBackIcon />
              <span>{sentBackTo}</span>
            </div>
          )}
          {tooltipData.splitStep ? (
            <AssigneesJointAppointment tooltipData={tooltipData} />
          ) : (
            <Assignees tooltipData={tooltipData} />
          )}
          {(tooltipData.state === IN_PROGRESS_STATUS ||
            tooltipData.state === SUBMITTED_STATUS) && (
            <div className="view-submissions" onKeyDown={closeWithTab}>
              <a
                className="navigation-link"
                href={tooltipData.submissionUrl}
                target="_blank"
              >
                {translate("app.home.reviewsInProgress.viewSubmissions")}
                <span className="tab-icon">
                  <NewTabIcon />
                </span>
              </a>
            </div>
          )}
        </div>
      </>
    )
  );
}

interface TooltipStateProps {
  tooltipData: TooltipData | null;
}

function completed(date: string) {
  return useIntl().formatMessage(
    { id: "app.home.reviewsInProgress.completed" },
    {
      date: formatDateFullYear(date)
    }
  );
}
function dueDate(date: string) {
  return useIntl().formatMessage(
    { id: "app.home.taskDueDate" },
    {
      date: formatDateFullYear(date)
    }
  );
}

function TooltipDate({ tooltipData }: TooltipStateProps) {
  const dueDays = useIntl().formatMessage(
    { id: "app.home.reviewsInProgress.dueDays" },
    {
      daysNumber: tooltipData?.daysUntilDue
    }
  );

  return (
    tooltipData && (
      <>
        {tooltipData.submittedDate ? (
          <div className="date">{completed(tooltipData.submittedDate)}</div>
        ) : (
          <>
            {tooltipData.dueDateType === FIXED ? (
              <div className="date">
                {tooltipData.dueDate && dueDate(tooltipData.dueDate)}
              </div>
            ) : (
              <div className="date">{dueDays}</div>
            )}
          </>
        )}
      </>
    )
  );
}

function Assignees({ tooltipData }: TooltipStateProps) {
  const { sysUser } = useContext<AppContextInterface>(AppContext);
  function checkCurrentUserIsAssignee(assignee: Assignee) {
    return (
      sysUser?.firstName === assignee.firstName &&
      sysUser.lastName === assignee.lastName
    );
  }
  return (
    <div className="assignee">
      <span>{translate("app.home.reviewsInProgress.assignedTo")} </span>
      {tooltipData?.assignees.map((assignee: Assignee, index: number) => {
        return (
          <div key={`${tooltipData.id} ${index}`}>
            {!checkCurrentUserIsAssignee(assignee) ? (
              <>
                <span> {`${assignee.firstName}`}</span>
                <span>{assignee.middleName ? assignee.middleName : ""}</span>
                <span> {assignee.lastName}</span>
              </>
            ) : (
              translate("app.home.reviewsInProgress.you")
            )}
            {index < tooltipData.assignees.length - 1 ? "," : ""}
          </div>
        );
      })}
    </div>
  );
}

function AssigneesJointAppointment({ tooltipData }: TooltipStateProps) {
  function checkSubmittedOrInProgress() {
    return (
      tooltipData?.state === IN_PROGRESS_STATUS ||
      tooltipData?.state === SUBMITTED_STATUS
    );
  }
  return (
    <div className="assignee-split-step">
      <div className="assignedTo">
        {translate("app.home.reviewsInProgress.assignedToSplitStep")}
      </div>
      {tooltipData?.assignees.map((assignee: Assignee, index: number) => {
        return (
          <div
            className={
              checkSubmittedOrInProgress()
                ? "assignees-list flex-row"
                : "assignees-list"
            }
            key={`${tooltipData.id} ${index}`}
          >
            {checkSubmittedOrInProgress() && (
              <div className="assignee-icon">
                {assignee.status === SUBMITTED_STATUS && <TickIcon />}
                {assignee.status === IN_PROGRESS_STATUS && <ClockIcon />}
              </div>
            )}
            <div>
              <div className="assignee-name">
                <span>{`${assignee.firstName} ${assignee.lastName}`}</span>
              </div>
              {checkSubmittedOrInProgress() && (
                <>
                  {assignee.submittedDate ? (
                    <div className="assignee-date">
                      {completed(assignee.submittedDate)}
                    </div>
                  ) : (
                    assignee.dueDate && (
                      <div className="assignee-date">
                        {dueDate(assignee.dueDate)}
                      </div>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
